import {
  ReactElement, useCallback, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '~/contexts/newToastContext';
import { HistoryCard } from '../components/History';
import { HistoryCardLoading } from '../components/Loading/History';
import { useFilterContext } from '../contexts/FilterContext';
import { getThermalComfortHistory } from '../helpers/overview';
import { parseHistoryData } from '../helpers/thermalComfort';
import { ThermalComfortGraph } from './components/Graph';
import { ThermalComfortHistoryInfo } from './types';

interface ThermalComfortHistoryCardProps {
  loading: boolean;
  hasThermalComfort: boolean;
}

export function ThermalComfortHistoryCard({
  hasThermalComfort,
  loading,
}: Readonly<ThermalComfortHistoryCardProps>): ReactElement {
  const [historyData, setHistoryData] = useState<ThermalComfortHistoryInfo>();
  const [loadingGraph, setLoadingGraph] = useState(true);

  const { clients, selectedOptions } = useFilterContext();
  const { toast } = useToast();
  const { t } = useTranslation();

  const getHistory = useCallback(async () => {
    setLoadingGraph(true);

    const clientIds = clients.map(Number);
    const data = await getThermalComfortHistory(clientIds, {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.units?.map((item) => Number(item.value)) ?? selectedOptions.group?.units,
      thermalComfortStatus: selectedOptions.status,
      cityIds: selectedOptions.cities?.map((item) => item.value),
    });

    if (data) {
      setHistoryData(parseHistoryData(data));
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosHistoricoConfortoTermico'), 'error');
    }

    setLoadingGraph(false);
  }, [clients, selectedOptions]);

  useEffect(() => {
    if (clients && clients.length > 0 && hasThermalComfort && !loading) {
      getHistory();
    }
  }, [clients, selectedOptions, hasThermalComfort, loading]);

  useEffect(() => {
    if (!hasThermalComfort || loading) {
      setHistoryData(undefined);
      setLoadingGraph(false);
    }
  }, [hasThermalComfort, loading]);

  if (loading || (loadingGraph && hasThermalComfort)) {
    return (
      <HistoryCardLoading />
    );
  }

  return (
    <HistoryCard
      id="thermal-comfort-history"
      dataTestId="thermal-comfort-history-card"
      description={t('mediaPorcentagemMesStatusConforto')}
      title={t('historicoConfortoTermico')}
      disabledTooltip={!hasThermalComfort}
      beta
    >
      <ThermalComfortGraph data={historyData} />
    </HistoryCard>
  );
}
